import React, { useRef, useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { ArrowLeft, BarChart3, Edit3, Save, X, Printer, FileSpreadsheet, RotateCw, Trash2 } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { createModal } from 'react-modal-promise';
import GeneralModal from '../../helpers/GeneralModal';
import LogicContext from '../../Context/LogicContext';
import { budgetService } from '../../services/budgetService';
import DelgroProgressBar from '../../generals/DelgroProgressBar';
import BudgetScroll from './BudgetScroll';
import BudgetDetailsView from './BudgetDetailsView';
import AccountPickerModalShared from './AccountPickerModal';
import BudgetEditForm from './BudgetEditForm';
import BudgetReportsModal from './BudgetReportsModal';
import { buildBudgetDetailsExportRows, downloadSpreadsheet as downloadBudgetSpreadsheet, safeExportName as safeBudgetExportName } from './budgetReportExports';
import {
  buildAccountRows,
  buildBudgetXml,
  buildBudgetVsActualGroups,
  buildBalanceSheetGroups,
  collapseMonthsToTerm,
  getBudgetPeriods,
  groupAccountsByMain,
  normalizeBudgetDetails,
  sumMonths,
} from './budgetDataHelpers';

function getBalanceSheetDefaultDate(financialYearCode) {
  const year = String(financialYearCode || '').match(/\d{4}/)?.[0];
  return year ? `${year}-12-31` : '';
}

function getAccountSection(account) {
  const name = account?.account_main_name?.trim() || 'Other';
  const identifier = account?.account_main_id ?? name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  return {
    key: `account-main-${identifier}`,
    title: `${name} Accounts`,
  };
}

function normalizeMasterData(serverData) {
  const data = Array.isArray(serverData) ? serverData[0] : serverData;
  return {
    account_head_info: data?.account_head_info || [],
    financial_year: data?.financial_year || [],
    budget_term: data?.budget_term || [],
  };
}

function BudgetDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);
  const printRef = useRef(null);
  const myPromiseModal = createModal(GeneralModal);

  const url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];

  const [budgetRows, setBudgetRows] = useState([]);
  const [masterData, setMasterData] = useState({
    account_head_info: [],
    financial_year: [],
    budget_term: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [onlyFunded, setOnlyFunded] = useState(false);

  // Edit states
  const [editBudgetName, setEditBudgetName] = useState('');
  const [editFinancialYearCode, setEditFinancialYearCode] = useState('');
  const [editBudgetTermId, setEditBudgetTermId] = useState('1');
  const [selectedIds, setSelectedIds] = useState({});
  const [amounts, setAmounts] = useState({});
  const [pickerSection, setPickerSection] = useState(null);
  const [formError, setFormError] = useState('');
  const [isVsActualOpen, setIsVsActualOpen] = useState(false);
  const [isVsActualLoading, setIsVsActualLoading] = useState(false);
  const [vsActualGroups, setVsActualGroups] = useState([]);
  const [vsActualError, setVsActualError] = useState(false);
  const [balanceSheetDate, setBalanceSheetDate] = useState('');
  const [balanceSheetGroups, setBalanceSheetGroups] = useState([]);
  const [isBalanceSheetLoading, setIsBalanceSheetLoading] = useState(false);
  const [balanceSheetError, setBalanceSheetError] = useState(false);

  const loadDetailsData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [detailsRes, masterRes] = await Promise.all([
        budgetService.budgetDetails(id),
        budgetService.getBBudgetmasterData().catch(() => null),
      ]);
      const normalizedDetails = normalizeBudgetDetails(detailsRes?.data);
      setBudgetRows(normalizedDetails);

      if (masterRes?.data) {
        setMasterData(normalizeMasterData(masterRes.data));
      }
    } catch (err) {
      console.error(err);
      logicContext.showToast('Failed to load budget details', 'error');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = 'Budget Details';
    loadDetailsData();
  }, [loadDetailsData]);

  const info = budgetRows?.[0];

  // Initialize edit form when starting edit mode
  const startEditing = () => {
    if (!info) return;

    setEditBudgetName(info.budget_name || '');
    setEditFinancialYearCode(String(info.financial_year_code || ''));
    const termId = String(info.budget_term_id || 1);
    setEditBudgetTermId(termId);

    const accountsList = buildAccountRows(budgetRows);
    const periodsList = getBudgetPeriods(termId);
    const initialSelected = {};
    const initialAmounts = {};

    accountsList.forEach(account => {
      const masterAccount = masterData.account_head_info.find(item => item.account_id === account.account_id);
      const section = getAccountSection(masterAccount || account);
      if (!initialSelected[section.key]) initialSelected[section.key] = [];
      initialSelected[section.key].push(account.account_id);
      const periodAmounts = collapseMonthsToTerm(account.months, termId);
      initialAmounts[account.account_id] = {};
      periodsList.forEach((period, idx) => {
        initialAmounts[account.account_id][period.term_id] = periodAmounts[idx] || 0;
      });
    });

    setSelectedIds(initialSelected);
    setAmounts(initialAmounts);
    setFormError('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setFormError('');
  };

  const accounts = useMemo(() => buildAccountRows(budgetRows), [budgetRows]);

  const visibleAccounts = useMemo(() => {
    let list = accounts;
    if (onlyFunded) {
      list = list.filter(account => account.total !== 0);
    }
    if (searchText.trim()) {
      const query = searchText.toLowerCase();
      list = list.filter(
        account =>
          account.account_name?.toLowerCase().includes(query) ||
          account.account_type_name?.toLowerCase().includes(query) ||
          account.account_main_name?.toLowerCase().includes(query)
      );
    }
    return list;
  }, [accounts, onlyFunded, searchText]);

  const groups = useMemo(() => groupAccountsByMain(visibleAccounts), [visibleAccounts]);

  const budgetTermId = info?.budget_term_id || 1;
  const periods = useMemo(() => getBudgetPeriods(budgetTermId), [budgetTermId]);
  const toPeriods = useCallback(months => collapseMonthsToTerm(months, budgetTermId), [budgetTermId]);

  const grandMonths = useMemo(() => sumMonths(visibleAccounts), [visibleAccounts]);
  const grandTotal = useMemo(() => visibleAccounts.reduce((sum, account) => sum + account.total, 0), [visibleAccounts]);
  const fundedCount = useMemo(() => accounts.filter(account => account.total !== 0).length, [accounts]);

  // Edit helpers
  const editPeriods = useMemo(() => getBudgetPeriods(editBudgetTermId), [editBudgetTermId]);

  const accountsById = useMemo(() => {
    const map = new Map();
    masterData.account_head_info.forEach(account => map.set(account.account_id, account));
    return map;
  }, [masterData.account_head_info]);

  const visibleSections = useMemo(() => {
    const sectionMap = new Map();

    const addSection = (account, includeInPicker) => {
      const section = getAccountSection(account);
      if (!sectionMap.has(section.key)) {
        sectionMap.set(section.key, { ...section, accounts: [] });
      }
      if (includeInPicker) {
        sectionMap.get(section.key).accounts.push(account);
      }
    };

    masterData.account_head_info.forEach(account => addSection(account, true));
    accounts.forEach(account => addSection(account, false));

    return [...sectionMap.values()]
      .map(section => ({
        ...section,
        accounts: section.accounts.sort((a, b) => (a.account_name || '').localeCompare(b.account_name || '')),
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [accounts, masterData.account_head_info]);

  const accountsBySection = useMemo(
    () => Object.fromEntries(visibleSections.map(section => [section.key, section.accounts])),
    [visibleSections]
  );

  const onEditBudgetTermChange = value => {
    const hasAmounts = Object.values(amounts).some(row => Object.values(row || {}).some(amount => Number(amount) !== 0));
    setEditBudgetTermId(value);
    if (hasAmounts) {
      setAmounts({});
      logicContext.showToast('Amounts were cleared because the budget period changed', 'info');
    }
  };

  const onPickerConfirm = accountIds => {
    setSelectedIds(prev => ({ ...prev, [pickerSection.key]: accountIds }));
    setPickerSection(null);
  };

  const removeAccount = (sectionKey, accountId) => {
    setSelectedIds(prev => ({
      ...prev,
      [sectionKey]: (prev[sectionKey] || []).filter(idItem => idItem !== accountId),
    }));
    setAmounts(prev => {
      const next = { ...prev };
      delete next[accountId];
      return next;
    });
  };

  const setAmount = (accountId, termId, value) => {
    setAmounts(prev => ({
      ...prev,
      [accountId]: { ...prev[accountId], [termId]: value },
    }));
  };

  const fillRowAcross = accountId => {
    const row = amounts[accountId] || {};
    const source = editPeriods.map(period => row[period.term_id]).find(value => value !== undefined && value !== '');
    if (source === undefined) return;

    const filled = {};
    editPeriods.forEach(period => {
      filled[period.term_id] = source;
    });
    setAmounts(prev => ({ ...prev, [accountId]: filled }));
  };

  const rowTotal = useCallback(
    accountId => {
      const row = amounts[accountId] || {};
      return editPeriods.reduce((sum, period) => sum + (Number(row[period.term_id]) || 0), 0);
    },
    [amounts, editPeriods]
  );

  const budgetLines = useMemo(() => {
    const lines = [];
    visibleSections.forEach(section => {
      (selectedIds[section.key] || []).forEach(accountId => {
        editPeriods.forEach(period => {
          lines.push({
            account_id: accountId,
            term_id: period.term_id,
            amount: Number(amounts[accountId]?.[period.term_id]) || 0,
          });
        });
      });
    });
    return lines;
  }, [visibleSections, selectedIds, editPeriods, amounts]);

  const editGrandTotal = useMemo(() => budgetLines.reduce((sum, line) => sum + line.amount, 0), [budgetLines]);

  const onSave = async () => {
    setFormError('');

    if (!editBudgetName.trim()) return setFormError('Budget name is required');
    if (!editFinancialYearCode) return setFormError('Fiscal year is required');
    if (!editBudgetTermId) return setFormError('Budget period is required');
    if (!budgetLines.length) return setFormError('Add at least one account to the budget');
    if (!budgetLines.some(line => line.amount !== 0)) {
      return setFormError('Enter an amount for at least one account');
    }

    const result = await myPromiseModal({
      title: 'Update Budget',
      body: `Save changes to budget "${editBudgetName.trim()}"?`,
      showInput: false,
    });
    if (!result?.decision) return;

    setSaving(true);
    try {
      const payload = {
        budget_ref_no: info?.budget_ref_no ?? Number(id),
        financial_year_code: Number(editFinancialYearCode),
        budget_term_id: Number(editBudgetTermId),
        budget_name: editBudgetName.trim(),
        xml_budget: buildBudgetXml(budgetLines),
        option: 2,
      };

      const response = await budgetService.submitBudget(payload);
      const { message_text, message_id } = response.data?.[0] || response.data?.result_status?.[0] || {};

      if (message_id === 1) {
        logicContext?.showToast?.(message_text || 'Budget updated successfully', 'success');
        setIsEditing(false);
        await loadDetailsData();
      } else {
        logicContext?.showToast?.(message_text || 'Failed to update budget', 'error');
      }
    } catch (err) {
      console.error('Failed to update budget', err);
      logicContext?.showToast?.('Failed to update budget', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const targetId = info?.budget_ref_no ?? id;
    const result = await myPromiseModal({
      title: 'Delete Budget',
      body: `Are you sure you want to delete budget "${info?.budget_name || targetId}"? This action cannot be undone.`,
      showInput: false,
    });
    if (!result?.decision) return;

    setDeleting(true);
    try {
      const response = await budgetService.deleteBudget(targetId);
      const { message_text, message_id } = response?.data?.[0] || response?.data?.result_status?.[0] || {};

      if (message_id === 1 || response?.status === 200) {
        logicContext?.showToast?.(message_text || 'Budget deleted successfully', 'success');
        navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}`);
      } else {
        logicContext?.showToast?.(message_text || 'Failed to delete budget', 'error');
      }
    } catch (err) {
      console.error('Failed to delete budget', err);
      logicContext?.showToast?.('Failed to delete budget', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Budget_${info?.budget_name || id}`,
  });

  const exportBudgetDetailsExcel = () => {
    if (!visibleAccounts.length) {
      logicContext?.showToast?.("No budget details to export", "info");
      return;
    }
    const rows = buildBudgetDetailsExportRows({ info, groups, periods, grandMonths, grandTotal, budgetTermId });
    downloadBudgetSpreadsheet("Budget Details", rows, `Budget_Details_${safeBudgetExportName(info?.budget_name || id)}`);
    logicContext?.showToast?.("Budget details exported to Excel", "success");
  };

  const loadBalanceSheet = async () => {
    if (!balanceSheetDate) return;

    setIsBalanceSheetLoading(true);
    setBalanceSheetError(false);
    try {
      const response = await budgetService.budgetBalanceSheet(balanceSheetDate);
      setBalanceSheetGroups(buildBalanceSheetGroups(response?.data));
    } catch (err) {
      console.error('Failed to load balance sheet', err);
      setBalanceSheetError(true);
      logicContext?.showToast?.('Failed to load balance sheet', 'error');
    } finally {
      setIsBalanceSheetLoading(false);
    }
  };

  const openBudgetVsActual = async () => {
    const defaultBalanceSheetDate = balanceSheetDate || getBalanceSheetDefaultDate(info?.financial_year_code);
    setBalanceSheetDate(defaultBalanceSheetDate);
    setBalanceSheetGroups([]);
    setBalanceSheetError(false);
    setIsVsActualOpen(true);
    setIsVsActualLoading(true);
    setVsActualError(false);
    setVsActualGroups([]);

    try {
      const response = await budgetService.budgetVsActual(id);
      setVsActualGroups(buildBudgetVsActualGroups(normalizeBudgetDetails(response?.data)));
    } catch (err) {
      console.error('Failed to load budget vs actual', err);
      setVsActualError(true);
      logicContext?.showToast?.('Failed to load budget vs actual', 'error');
    } finally {
      setIsVsActualLoading(false);
    }
  };

  const viewItemDetails = useCallback(
    budgetId => {
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${budgetId}`);
    },
    [navigate, currModuleId, currModuleName, currUserMenu]
  );

  const iconBtn =
    'flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-800';
  const monthCell = 'px-3 py-3 text-right tabular-nums whitespace-nowrap';
  const stickyCell = 'sticky left-0 z-10 px-4 py-3 text-left';

  return (
    <section className="mt-2 px-3 pb-12">
      <div className="mx-auto max-w-[1600px]">
        {/* Toolbar */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <button onClick={() => navigate(-1)} className={iconBtn} aria-label="Go back">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-semibold text-gray-800">{info?.budget_name || 'Budget details'}</h1>
              <p className="truncate text-sm text-gray-500">Budget · Ref {info?.budget_ref_no ?? id}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!isEditing ? (
              <>
                <button
                  onClick={startEditing}
                  disabled={isLoading || !info || deleting}
                  className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
                  title="Edit Budget"
                >
                  <Edit3 className="h-4 w-4" />
                  Edit
                </button>
                <button
                  onClick={openBudgetVsActual}
                  disabled={isLoading || !info || deleting}
                  className="flex h-9 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100 disabled:opacity-60"
                  title="View Budget vs Actual"
                >
                  <BarChart3 className="h-4 w-4" />
                  Budget vs Actual
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isLoading || !info || deleting}
                  className="flex h-9 items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 shadow-sm transition hover:bg-red-100 disabled:opacity-60"
                  title="Delete Budget"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
                <button onClick={loadDetailsData} className={iconBtn} title="Reload">
                  <RotateCw className="h-4 w-4" />
                </button>
                <button onClick={handlePrint} className={iconBtn} title="Export budget details as PDF">
                  <Printer className="h-4 w-4" />
                </button>
                <button onClick={exportBudgetDetailsExcel} className={iconBtn} title="Export budget details as Excel">
                  <FileSpreadsheet className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onSave}
                  disabled={saving}
                  className="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={cancelEditing}
                  disabled={saving}
                  className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-100"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Left sidebar - budget list */}
          <div className="w-full shrink-0 lg:w-72">
            <BudgetScroll viewItemDetails={viewItemDetails} />
          </div>

          {/* Main details / edit card */}
          {isLoading ? (
            <div className="min-w-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm"><DelgroProgressBar /></div>
          ) : isEditing ? (
            <div className="min-w-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"><div className="p-6 lg:p-8"><BudgetEditForm info={info} id={id} masterData={masterData} budgetName={editBudgetName} financialYearCode={editFinancialYearCode} budgetTermId={editBudgetTermId} periods={editPeriods} visibleSections={visibleSections} accountsById={accountsById} selectedIds={selectedIds} amounts={amounts} total={editGrandTotal} error={formError} saving={saving} onBudgetNameChange={setEditBudgetName} onFinancialYearChange={setEditFinancialYearCode} onBudgetTermChange={onEditBudgetTermChange} onAddAccounts={setPickerSection} onRemoveAccount={removeAccount} onAmountChange={setAmount} onFillAcross={fillRowAcross} rowTotal={rowTotal} onSave={onSave} onCancel={cancelEditing} /></div></div>
          ) : (
            <BudgetDetailsView printRef={printRef} info={info} id={id} accounts={accounts} groups={groups} periods={periods} grandMonths={grandMonths} grandTotal={grandTotal} fundedCount={fundedCount} isLoading={isLoading} searchText={searchText} onlyFunded={onlyFunded} onSearchChange={setSearchText} onOnlyFundedChange={setOnlyFunded} toPeriods={toPeriods} />
          )}
        </div>
      {pickerSection && (
        <AccountPickerModalShared
          title={`Add ${pickerSection.title}`}
          accounts={accountsBySection[pickerSection.key] || []}
          selectedIds={selectedIds[pickerSection.key] || []}
          onCancel={() => setPickerSection(null)}
          onConfirm={onPickerConfirm}
        />
      )}
      {isVsActualOpen && (
        <BudgetReportsModal
          budgetName={info?.budget_name}
          budgetTermId={budgetTermId}
          financialYearCode={info?.financial_year_code}
          groups={vsActualGroups}
          isLoading={isVsActualLoading}
          error={vsActualError}
          balanceSheetDate={balanceSheetDate}
          balanceSheetGroups={balanceSheetGroups}
          isBalanceSheetLoading={isBalanceSheetLoading}
          balanceSheetError={balanceSheetError}
          onBalanceSheetDateChange={value => {
            setBalanceSheetDate(value);
            setBalanceSheetGroups([]);
            setBalanceSheetError(false);
          }}
          onLoadBalanceSheet={loadBalanceSheet}
          onClose={() => setIsVsActualOpen(false)}
        />
      )}
      </div>
    </section>
  );
}

export default BudgetDetailsPage;
